import { MeasureHelper } from '@core/helpers/measure.helper';
import { StringHelper } from '@core/helpers/string.helper';
import { Measure } from '@core/models/aggregated-data.model';
import { Alarm } from '@core/models/alarm.model';
import { ALARM_THRESHOLD_CATEGORIES, AlarmThresholdCategory, ThresholdType } from '@core/models/threshold.model';
import { MeasuringPointType } from '@shared/gui/models/measuringPointType.enum';

export class AlarmHelper {
  public static isSameAlarms(a1: Alarm, a2: Alarm): boolean {
    return (
      a1.simulationTime === a2.simulationTime &&
      a1.key === a2.key &&
      a1.simulationType === a2.simulationType &&
      a1.attribute === a2.attribute &&
      a1.isReplacement === a2.isReplacement &&
      a1.thresholdType === a2.thresholdType
    );
  }

  public static hasAlarm(property: string, alarms: Array<Alarm>, measure?: Measure): boolean {
    if (!property || !alarms || alarms.length === 0) {
      return false;
    }

    if (!measure) {
      return alarms.some(x => x.attribute === property);
    } else {
      const measureType = MeasureHelper.getMeasureType(property);
      if (
        measureType === MeasuringPointType.PREVIOUS ||
        measureType === MeasuringPointType.DEFAULT ||
        measureType === MeasuringPointType.CORRECTED
      ) {
        const alarm = alarms.find(x => x.attribute.startsWith(MeasureHelper.getName(property)));
        if (alarm) {
          if (!StringHelper.isNullOrEmpty(alarm.systemHighLimit) && measure.value > alarm.systemHighLimit) {
            return true;
          }
          if (!StringHelper.isNullOrEmpty(alarm.globalHighLimit) && measure.value > alarm.globalHighLimit) {
            return true;
          }
          if (!StringHelper.isNullOrEmpty(alarm.customHighLimit) && measure.value > alarm.customHighLimit) {
            return true;
          }
          if (!StringHelper.isNullOrEmpty(alarm.systemLowLimit) && measure.value < alarm.systemLowLimit) {
            return true;
          }
          if (!StringHelper.isNullOrEmpty(alarm.globalLowLimit) && measure.value < alarm.globalLowLimit) {
            return true;
          }
          if (!StringHelper.isNullOrEmpty(alarm.customLowLimit) && measure.value < alarm.customLowLimit) {
            return true;
          }
          //CONFLICT ?
        } else {
          return false;
        }
      } else {
        return alarms.some(x => x.attribute === property && x.isReplacement === !!measure.isReplacement);
      }
    }
  }

  public static exceededThresholdCategories(
    alarms: Array<Alarm>,
    valueOf: (alarm: Alarm) => number
  ): Array<AlarmThresholdCategory> {
    return ALARM_THRESHOLD_CATEGORIES.filter(category => {
      const prefix = category.toLowerCase() as Lowercase<AlarmThresholdCategory>;
      return (alarms ?? []).some(alarm => {
        const value = valueOf(alarm);
        const highLimit = alarm[`${prefix}HighLimit`];
        const lowLimit = alarm[`${prefix}LowLimit`];
        return (
          (!StringHelper.isNullOrEmpty(highLimit) && value > highLimit) ||
          (!StringHelper.isNullOrEmpty(lowLimit) && value < lowLimit)
        );
      });
    });
  }

  public static applicableAlarms(input: string | number, alarms: Array<Alarm>): Array<Alarm> {
    const value = parseFloat('' + input);
    if (StringHelper.isNullOrEmpty(input)) {
      return alarms;
    } else {
      return alarms.filter(alarm => {
        if (!StringHelper.isNullOrEmpty(alarm.systemHighLimit) && value > alarm.systemHighLimit) {
          return true;
        }
        if (!StringHelper.isNullOrEmpty(alarm.globalHighLimit) && value > alarm.globalHighLimit) {
          return true;
        }
        if (!StringHelper.isNullOrEmpty(alarm.customHighLimit) && value > alarm.customHighLimit) {
          return true;
        }
        if (!StringHelper.isNullOrEmpty(alarm.systemLowLimit) && value < alarm.systemLowLimit) {
          return true;
        }
        if (!StringHelper.isNullOrEmpty(alarm.globalLowLimit) && value < alarm.globalLowLimit) {
          return true;
        }
        if (!StringHelper.isNullOrEmpty(alarm.customLowLimit) && value < alarm.customLowLimit) {
          return true;
        }
        if (!StringHelper.isNullOrEmpty(alarm.conflict)) {
          return true;
        }
        if (alarm.thresholdType === ThresholdType.INCOHERENT && value >= -1) {
          return true;
        }
        return false;
      });
    }
  }
}
